<template>
  <div class="table-wrapper">
    <nova-atividade-modal
      ref="novaAtividade"
      :deal-id="dealId"
      @created="loadActivities"
      @updated="loadActivities"
    />

    <!-- MODAL DE VISUALIZAÇÃO -->
    <b-modal
      id="atividade-modal"
      ref="atividadeModal"
      hide-footer
      size="lg"
      title="Detalhes da Atividade"
    >
      <div
        v-if="atividadeSelecionada"
        class="pb-3"
      >

        <!-- BADGE -->
        <div class="d-flex align-items-center justify-content-between mb-3">
          <b-badge
            variant="primary"
            class="d-flex align-items-center"
          >
            <b-form-select
              v-model="atividadeEdit.status"
              :options="statusSelectOptions"
              class="p-0"
              style="min-width: 140px;"
            />
          </b-badge>
        </div>

        <!-- GRID DE INFORMAÇÕES PRINCIPAIS -->
        <b-row>
          <b-col
            cols="6"
            class="mb-2"
          >
            <strong>Período:</strong>
            <div class="d-flex flex-column">
              <span class="small text-muted">Início</span>
              <input
                v-model="atividadeEdit.startDate"
                type="datetime-local"
                class="form-control mb-2"
              >
              <span class="small text-muted">Término</span>
              <input
                v-model="atividadeEdit.endDate"
                type="datetime-local"
                class="form-control"
              >
            </div>
          </b-col>

          <b-col
            cols="6"
            class="mb-2"
          >
            <strong>Responsável:</strong>
            <div>{{ atividadeSelecionada.usuCadastro || '-' }}</div>
          </b-col>

          <b-col
            cols="6"
            class="mb-2"
          >
            <strong>Criado em:</strong>
            <div>{{ atividadeSelecionada.dataCadastro || '-' }}</div>
          </b-col>

          <b-col
            cols="6"
            class="mb-2"
          >
            <strong>Última atualização:</strong>
            <div>{{ atividadeSelecionada.dataAlteracao || '-' }}</div>
          </b-col>
        </b-row>

        <hr>

        <!-- DESCRIÇÃO -->
        <div class="mb-3">
          <strong>Descrição:</strong>
          <b-form-textarea
            v-model="atividadeEdit.description"
            rows="3"
          />
        </div>

        <hr>

        <!-- ANEXOS -->
        <div class="mb-3">
          <strong>Anexos:</strong>
          <div class="mt-2">
            <div class="d-flex align-items-center mb-2">
              <input
                type="file"
                :disabled="uploading"
                @change="onFileSelected"
              >
              <div
                v-if="uploading"
                class="ml-2 text-muted"
              >
                Enviando...
              </div>
            </div>

            <div v-if="attachments && attachments.length">
              <ul class="mb-0">
                <li
                  v-for="att in attachments"
                  :key="att.id"
                  class="d-flex align-items-center justify-content-between py-1"
                >
                  <div>
                    <strong>
                      {{ att.fileName || att.fileNameDisplay || att.fileNameOriginal || att.nome || 'Anexo' }}
                    </strong>
                    <div class="text-muted small">
                      {{ att.uploadedBy || att.usuario || att.usuCadastro || '' }}
                    </div>
                  </div>
                  <div>
                    <b-button
                      size="sm"
                      variant="link"
                      @click="viewAttachment(att)"
                    >
                      Visualizar
                    </b-button>
                    <b-button
                      size="sm"
                      variant="link"
                      @click="downloadAttachment(att)"
                    >
                      Baixar
                    </b-button>
                    <b-button
                      size="sm"
                      variant="danger"
                      class="ml-2"
                      @click="deleteAttachment(att)"
                    >
                      Excluir
                    </b-button>
                  </div>
                </li>
              </ul>
            </div>
            <div v-else>
              <p class="text-muted mb-0">
                Nenhum anexo enviado.
              </p>
            </div>
          </div>
          <visualizar-arquivo
            v-if="viewingFileUrl"
            ref="visualizador"
            :url="viewingFileUrl"
            :observacao="viewingObservacao"
            :download="true"
            @hidden="viewingFileUrl = ''"
          />
        </div>

        <hr>

        <!-- HISTÓRICO -->
        <div class="mb-3">
          <strong>Histórico:</strong>
          <div class="text-muted mb-0">
            <div v-if="logs && logs.length">
              <div
                v-for="log in logs"
                :key="log.id || log.dataHora || JSON.stringify(log)"
              >
                <div style="font-weight:600">
                  <span>
                    {{ log.dataCadastro || formatDate(log.createdAt) || log.createdAt || '' }}
                  </span>
                  <small class="text-muted">
                    {{ log.createdBy || log.usuCadastro || log.user || '' }}
                  </small>
                </div>
                <div class="mb-2">
                  {{ log.action || log.message || log.description || '' }}
                </div>
                <hr>
              </div>
            </div>
            <div v-else>
              <em>Nenhum registro de log encontrado para esta atividade.</em>
            </div>
          </div>
        </div>

        <hr>

        <!-- ALTERAR STATUS (local) -->
        <div class="mb-3">
          <strong>Tipo:</strong>
          <b-form-select
            v-model="atividadeEdit.type"
            :options="typeSelectOptions"
            class="mt-2"
          />
        </div>

        <!-- AÇÕES -->
        <div
          class="d-flex justify-content-end mt-4"
        >
          <b-button
            variant="primary"
            class="mr-2"
            @click="editarAtividade"
          >
            Salvar
          </b-button>
          <b-button
            variant="secondary"
            @click="$refs.atividadeModal.hide()"
          >
            Fechar
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- TABELA -->
    <table
      v-if="atividades && atividades.length"
      class="clean-table"
      style="margin-top: 1rem;"
    >
      <thead>
        <tr>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faTag"
                class="th-icon"
              />
              Tipo
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faCalendar"
                class="th-icon"
              />
              Prazo
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faCircleNotch"
                class="th-icon"
              />
              Status
            </span>
          </th>

          <th class="action-col">
            <span class="th-content" />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="atividade in atividades"
          :key="atividade.id"
        >
          <td>
            {{ atividade.type }}
          </td>
          <td>
            {{ formatDateRange(atividade.startDate, atividade.endDate) }}
          </td>
          <td>
            {{ atividade.status }}
          </td>

          <td class="actions">
            <font-awesome-icon
              :icon="faEye"
              class="action"
              @click="abrirModalCom(atividade)"
            />
            <font-awesome-icon
              :icon="faTrash"
              class="action"
              @click="excluirAtividade(atividade)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-else
      class="text-center my-4"
    >
      <em>Nenhuma atividade encontrada para esta oportunidade.</em>
    </div>
  </div>
</template>

<style>
/* Wrapper para scroll horizontal */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.titulo-td {
  max-width: 200px; /* ajuste conforme seu layout */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ----------------------
   TABLE BASE
------------------------- */
.clean-table {
  width: 100%;
  border-collapse: collapse;
  font-family: inherit;
  color: #333;
}

/* ----------------------
   TABLE HEAD
------------------------- */
.clean-table thead th {
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid #e2e2e2;
  font-size: 14px;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.th-icon {
  font-size: 16px;
  color: #777;
}

/* ----------------------
   TABLE BODY
------------------------- */
.clean-table tbody td {
  padding: 12px 8px;
  border-bottom: 1px solid #f1f1f1;
  font-size: 14px;
}

/* ----------------------
   ACTION COLUMN
------------------------- */
.action-col {
  width: 90px;
}

.actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action {
  cursor: pointer;
  color: #666;
  transition: 0.2s ease;
  font-size: 18px;
}

.action:hover {
  color: #000;
}

.action.edit:hover {
  color: #007bff;
}

.action.delete:hover {
  color: #dc3545;
}
</style>

<script>
import {
  faHeading, faCalendar, faUser, faEye, faPen, faTag, faTrash, faClock, faCircleNotch,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import api from '@/shared/components/atividades-tabela/shared/services/api'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'

const STATUS_VALUE_MAP = {
  pending: 1,
  pendente: 1,
  concluido: 2,
  concluído: 2,
  completed: 2,
  overdue: 3,
  atrasado: 3,
  canceled: 4,
  cancelled: 4,
  cancelado: 4,
  'in progress': 5,
  'in-progress': 5,
  inprogress: 5,
  'em andamento': 5,
  andamento: 5,
}

const OVERDUE_STATUS = 3
const IN_PROGRESS_STATUS = 5
const COMPLETED_STATUS = 2
const CANCELED_STATUS = 4

const mapTypeToValue = type => {
  if (!type) return 1
  switch (String(type).toLowerCase()) {
    case 'task':
      return 1
    case 'call':
      return 2
    case 'meeting':
      return 3
    case 'email':
      return 4
    default:
      return 1
  }
}

function normalizeStatusValue(status) {
  if (typeof status === 'number') return status
  if (!status) return 1
  const normalized = String(status).trim().toLowerCase()
  if (Object.prototype.hasOwnProperty.call(STATUS_VALUE_MAP, normalized)) {
    return STATUS_VALUE_MAP[normalized]
  }
  const numeric = Number(normalized)
  return Number.isNaN(numeric) ? 1 : numeric
}

const mapStatusStringToValue = status => normalizeStatusValue(status)

function toDateTimeLocalInput(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offsetMs = date.getTimezoneOffset() * 60000
  const local = new Date(date.getTime() - offsetMs)
  return local.toISOString().slice(0, 16)
}

function safelyParseDateTime(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function toLocalISOStringWithOffset(date) {
  if (!date) return null
  const pad = n => String(n).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())
  const tzOffset = -date.getTimezoneOffset()
  const sign = tzOffset >= 0 ? '+' : '-'
  const absOffset = Math.abs(tzOffset)
  const tzHours = pad(Math.floor(absOffset / 60))
  const tzMinutes = pad(absOffset % 60)
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${tzHours}:${tzMinutes}`
}

export default {
  name: 'AtividadesTabela',
  components: {
    FontAwesomeIcon,
    NovaAtividadeModal: () => import('@/shared/components/nova-atividade-modal/NovaAtividadeModal.vue'),
    VisualizarArquivo: () => import('@/shared/components/visualizar-arquivo/VisualizarArquivo.vue'),
  },
  props: {
    dealId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      faHeading,
      faCalendar,
      faUser,
      faEye,
      faPen,
      faTrash,
      faClock,
      faTag,
      faCircleNotch,
      atividades: [],
      atividadeSelecionada: null,
      atividadeEdit: {
        id: null,
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        status: 1,
        type: 1,
      },
      statusSelecionado: 'em_andamento',
      statusOptions: [
        { value: 'em_andamento', text: 'Em andamento' },
        { value: 'atrasado', text: 'Atrasado' },
        { value: 'finalizado', text: 'Finalizado' },
      ],
      // select options for editing
      typeSelectOptions: [
        { value: 1, text: 'Tarefa' },
        { value: 2, text: 'Ligação' },
        { value: 3, text: 'Reunião' },
        { value: 4, text: 'E-mail' },
      ],
      statusSelectOptions: [
        { value: 1, text: 'Pendente' },
        { value: 2, text: 'Concluído' },
        { value: 3, text: 'Atrasado' },
        { value: 4, text: 'Cancelado' },
        { value: 5, text: 'Em andamento' },
      ],
      overdueSyncInProgress: false,
      overdueCheckInterval: null,
      // attachments & logs
      attachments: [],
      logs: [],
      uploading: false,
      viewingFileUrl: '',
      viewingObservacao: '',
    }
  },
  watch: {
    dealId(newVal) {
      if (newVal) this.loadActivities()
    },
  },
  mounted() {
    this.startOverdueWatcher()
    if (this.dealId) this.loadActivities()
  },
  beforeDestroy() {
    this.stopOverdueWatcher()
  },
  methods: {
    async loadActivities(options = {}) {
      const { skipOverdueCheck = false } = options
      try {
        const res = await api.getDealActivities(this.dealId)
        this.atividades = res && res.data ? res.data : []
        if (!skipOverdueCheck) {
          this.checkOverdueActivities()
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar atividades', e)
      }
    },
    startOverdueWatcher() {
      this.stopOverdueWatcher()
      this.overdueCheckInterval = setInterval(() => {
        this.checkOverdueActivities()
      }, 20000)
    },
    stopOverdueWatcher() {
      if (this.overdueCheckInterval) {
        clearInterval(this.overdueCheckInterval)
        this.overdueCheckInterval = null
      }
    },
    async checkOverdueActivities() {
      if (!this.dealId || this.overdueSyncInProgress || !this.atividades || !this.atividades.length) return
      const now = Date.now()
      const candidates = this.atividades.filter(atividade => {
        const startDate = safelyParseDateTime(atividade.startDate)
        if (!startDate || startDate.getTime() > now) return false
        const statusValue = normalizeStatusValue(atividade.status)
        return ![OVERDUE_STATUS, IN_PROGRESS_STATUS, COMPLETED_STATUS, CANCELED_STATUS].includes(statusValue)
      })
      if (!candidates.length) return
      this.overdueSyncInProgress = true
      try {
        await Promise.all(candidates.map(atividade => this.markActivityAsOverdue(atividade)))
        await this.loadActivities({ skipOverdueCheck: true })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao atualizar status das atividades atrasadas', error)
      } finally {
        this.overdueSyncInProgress = false
      }
    },
    async markActivityAsOverdue(atividade) {
      const payload = {
        Id: atividade.id,
        Title: atividade.title || atividade.titulo || '',
        Description: atividade.description || atividade.descricao || null,
        StartDate: atividade.startDate,
        EndDate: atividade.endDate,
        Status: OVERDUE_STATUS,
        Type: this.normalizeActivityType(atividade.type || atividade.atividade),
      }
      await api.updateActivity(payload)
    },
    normalizeActivityType(value) {
      if (typeof value === 'number') return value
      const numeric = Number(value)
      if (!Number.isNaN(numeric)) return numeric
      return mapTypeToValue(value)
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (Number.isNaN(d.getTime())) return date
      return d.toLocaleString()
    },
    formatDateRange(start, end) {
      const startValue = start ? this.formatDate(start) : ''
      const endValue = end ? this.formatDate(end) : ''
      if (!startValue && !endValue) return '-'
      if (!endValue) return startValue || '-'
      if (!startValue) return endValue
      return `${startValue} → ${endValue}`
    },
    abrirModal() {
      this.$refs.atividadeModal.show()
    },
    abrirModalCom(atividade) {
      this.atividadeSelecionada = atividade
      // populate editable copy
      this.atividadeEdit.id = atividade.id
      this.atividadeEdit.title = atividade.title || ''
      this.atividadeEdit.description = atividade.description || ''
      this.atividadeEdit.startDate = toDateTimeLocalInput(atividade.startDate)
      this.atividadeEdit.endDate = toDateTimeLocalInput(atividade.endDate)
      this.atividadeEdit.type = mapTypeToValue(atividade.type)
      this.atividadeEdit.status = mapStatusStringToValue(atividade.status)
      this.$refs.atividadeModal.show()
      // load attachments and logs for this activity
      this.loadAttachments(atividade.id)
      this.loadLogs(atividade.id)
    },

    async loadAttachments(activityId) {
      try {
        const res = await api.getAttachmentsByActivity(activityId)
        this.attachments = res && res.data ? res.data : []
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar anexos', e)
        this.attachments = []
      }
    },

    async onFileSelected(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      // simple client-side checks
      if (typeof this.isDangerFile === 'function' && this.isDangerFile(file.name)) {
        // eslint-disable-next-line no-console
        console.warn('Arquivo proibido')
        return
      }
      // upload
      await this.uploadAttachment(file)
      e.target.value = null
    },

    async uploadAttachment(file) {
      if (!this.atividadeSelecionada) return
      try {
        this.uploading = true
        const fd = new FormData()
        fd.append('activityId', this.atividadeSelecionada.id)
        fd.append('file', file)
        await api.createAttachment(fd)
        await this.loadAttachments(this.atividadeSelecionada.id)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao enviar anexo', e)
      } finally {
        this.uploading = false
      }
    },

    async viewAttachment(att) {
      try {
        const res = await api.downloadAttachment(att.id)
        const url = res && res.data ? res.data : null
        if (url) {
          this.viewingFileUrl = url
          this.viewingObservacao = att.observacao || ''
          this.$nextTick(() => {
            if (this.$refs.visualizador && typeof this.$refs.visualizador.show === 'function') {
              this.$refs.visualizador.show()
            }
          })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao obter URL do anexo', e)
      }
    },
    async downloadAttachment(att) {
      try {
        const res = await api.downloadAttachment(att.id)
        const url = res && res.data ? res.data : null
        if (url) {
          // trigger download
          const link = document.createElement('a')
          link.target = '_blank'
          link.href = url
          link.download = att.fileName || att.fileNameDisplay || att.fileNameOriginal || att.nome || 'anexo'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao baixar anexo', e)
      }
    },
    async deleteAttachment(att) {
      try {
        await api.deleteAttachment(att.id)
        await this.loadAttachments(this.atividadeSelecionada.id)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao deletar anexo', e)
      }
    },

    async loadLogs(activityId) {
      try {
        const res = await api.getLogsByActivity(activityId)
        this.logs = res && res.data ? res.data : []
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar logs', e)
        this.logs = []
      }
    },

    async editarAtividade() {
      try {
        const startDate = safelyParseDateTime(this.atividadeEdit.startDate)
        const endDate = safelyParseDateTime(this.atividadeEdit.endDate)

        // Validação: não permitir término anterior ao início
        if (startDate && endDate && endDate < startDate) {
          const msg = 'A data/hora de término não pode ser anterior à data/hora de início.'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
          return
        }

        // required validations
        if (!startDate) {
          this.$toast({ component: ToastificationContent, props: { title: 'Preencha a data/hora de início.', icon: 'AlertCircleIcon', variant: 'danger' } })
          return
        }
        if (!endDate) {
          this.$toast({ component: ToastificationContent, props: { title: 'Preencha a data/hora de término.', icon: 'AlertCircleIcon', variant: 'danger' } })
          return
        }

        const payload = {
          Id: this.atividadeEdit.id,
          Title: this.atividadeEdit.title,
          Description: this.atividadeEdit.description || null,
          StartDate: startDate ? toLocalISOStringWithOffset(startDate) : null,
          EndDate: endDate ? toLocalISOStringWithOffset(endDate) : null,
          Status: Number(this.atividadeEdit.status) || 1,
          Type: Number(this.atividadeEdit.type) || 1,
        }

        await api.updateActivity(payload)
        await this.loadActivities()
        this.$refs.atividadeModal.hide()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erro ao editar atividade', e)
      }
    },
    openEdit(atividade) {
      // open NovaAtividadeModal in edit mode via ref (robust: prefer method, fallback to direct population)
      const child = this.$refs.novaAtividade
      if (!child) {
        // eslint-disable-next-line no-console
        console.error('Ref novaAtividade não encontrada')
        return
      }

      if (typeof child.openForEdit === 'function') {
        try {
          child.openForEdit(atividade)
          this.$refs.atividadeModal.hide()
          return
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Erro chamando openForEdit, tentando fallback', e)
        }
      }

      // Fallback: set fields directly on child and show modal
      try {
        child.isEdit = true
        child.novaAtividade.id = atividade.id
        child.novaAtividade.titulo = atividade.title || ''
        child.novaAtividade.descricao = atividade.description || ''
        child.novaAtividade.startDate = toDateTimeLocalInput(atividade.startDate)
        child.novaAtividade.endDate = toDateTimeLocalInput(atividade.endDate)
        child.novaAtividade.atividade = mapTypeToValue(atividade.type)
        child.novaAtividade.status = mapStatusStringToValue(atividade.status)
        if (child.$refs && child.$refs.modalCadastroAtividade && typeof child.$refs.modalCadastroAtividade.show === 'function') {
          child.$refs.modalCadastroAtividade.show()
        }
        this.$refs.atividadeModal.hide()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Não foi possível abrir modal de edição', e)
      }
    },
    excluirAtividade(atividade) {
      if (!atividade || !atividade.id) return
      this.$confirmar(
        'Deseja realmente excluir esta atividade?',
        'Essa ação não pode ser desfeita.',
        'question', 'danger',
      ).then(result => {
        if (!result || !result.value) return
        this.$isLoading = true
        api.deleteActivity(atividade.id).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Atividade excluída com sucesso.',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
          this.loadActivities()
        })
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response.data.details
              ? error.response.data.details
              : 'Houve um erro ao tentar excluir a atividade.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.$isLoading = false
      })
    },
  },
}
</script>
